import { Clear, Search as SearchIcon } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageContentWrapper from "../../../../../components/wrappers/PageContentWrapper";
import ErrorFullScreenMessage from "../../../../../components/fullScreenMessage/ErrorFullScreenMessage";
import { GET_BLOOD_DONORS } from "../../../../../enums/ApiPaths";
import { BLOOD_DONORS_KEY } from "../../../../../enums/QueryKeys";
import { petViewUrl } from "../../../../../enums/UrlPaths";
import { useFetch } from "../../../../../hooks/Fetching";
import { useRedirectIfNotOwnClinic } from "../../../../../hooks/useRedirectIfNotOwnClinic";
import useUser from "../../../../../hooks/useUser";
import { useIsSmallerScreen } from "../../../../../utils/Functions";
import BloodDonorDetailsDialog from "./BloodDonorDetailsDialog";
import BloodDonorsTable from "./BloodDonorsTable";

const BLOOD_DONOR_PARAMS = {
  search: "search",
  sort: "sort",
  page: "page",
  size: "size",
};

const DEFAULT_SORT_FIELD = "name";
const DEFAULT_SORT_DIRECTION = "asc";
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 150;

function getOwnerDisplay(pet) {
  return pet?.owner || pet?.clinicName || "-";
}

function matchesSearch(donor, search) {
  if (!search) return true;
  const needle = search.toLowerCase();
  return (
    donor.name?.toLowerCase().includes(needle) ||
    donor.ownerDisplay?.toLowerCase().includes(needle) ||
    donor.phoneNumber?.toLowerCase().includes(needle) ||
    donor.bloodType?.toLowerCase().includes(needle)
  );
}

function compareDonors(a, b, orderBy, order) {
  const direction = order === "desc" ? -1 : 1;
  const aValue = a?.[orderBy] ?? "";
  const bValue = b?.[orderBy] ?? "";
  return direction * String(aValue).localeCompare(String(bValue));
}

/** Search/sort/pagination live in the URL (useSearchParams), matching Warehouse's WAREHOUSE_TABLE_PARAMS convention. */
export default function ClinicBloodDonors() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { roles, currentClinic } = useUser();
  const clinicId = roles.master ? id : currentClinic?.id;
  useRedirectIfNotOwnClinic(clinicId);
  const isSmallScreen = useIsSmallerScreen();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get(BLOOD_DONOR_PARAMS.search) || "";
  const [searchInput, setSearchInput] = React.useState(searchParam);
  const debounceRef = React.useRef(null);

  const sortParam = searchParams.get(BLOOD_DONOR_PARAMS.sort) || "";
  const [orderBy, order] = sortParam
    ? sortParam.split("=")
    : [DEFAULT_SORT_FIELD, DEFAULT_SORT_DIRECTION];
  const page =
    Number(searchParams.get(BLOOD_DONOR_PARAMS.page)) || DEFAULT_PAGE;
  const rowsPerPage =
    Number(searchParams.get(BLOOD_DONOR_PARAMS.size)) || DEFAULT_PAGE_SIZE;

  const [detailsDonorId, setDetailsDonorId] = React.useState(null);

  const url = clinicId ? `${GET_BLOOD_DONORS}?id=${clinicId}` : GET_BLOOD_DONORS;
  const {
    data = [],
    isLoading,
    error,
  } = useFetch(url, null, [BLOOD_DONORS_KEY, clinicId]);

  React.useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  React.useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  /** Always a single setSearchParams call per action - two sequential calls in the same tick build off the same stale `prev`, so the second silently overwrites the first. */
  const updateQueryParams = React.useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  function handleSearchInputChange(e) {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateQueryParams({ [BLOOD_DONOR_PARAMS.search]: value, [BLOOD_DONOR_PARAMS.page]: "" });
    }, SEARCH_DEBOUNCE_MS);
  }

  function handleClearSearch() {
    clearTimeout(debounceRef.current);
    setSearchInput("");
    updateQueryParams({ [BLOOD_DONOR_PARAMS.search]: "", [BLOOD_DONOR_PARAMS.page]: "" });
  }

  function handleSort(field, direction) {
    updateQueryParams({ [BLOOD_DONOR_PARAMS.sort]: `${field}=${direction}` });
  }

  function handlePageChange(_, newPage) {
    updateQueryParams({ [BLOOD_DONOR_PARAMS.page]: String(newPage) });
  }

  function handleRowsPerPageChange(e) {
    updateQueryParams({ [BLOOD_DONOR_PARAMS.size]: e.target.value, [BLOOD_DONOR_PARAMS.page]: "" });
  }

  const donors = React.useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((pet) => ({
        ...pet,
        ownerDisplay: getOwnerDisplay(pet),
      })),
    [data],
  );

  const filteredDonors = React.useMemo(
    () => donors.filter((donor) => matchesSearch(donor, searchParam)),
    [donors, searchParam],
  );

  const sortedDonors = React.useMemo(
    () => [...filteredDonors].sort((a, b) => compareDonors(a, b, orderBy, order)),
    [filteredDonors, orderBy, order],
  );

  const visibleDonors = sortedDonors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const detailsDonor = donors.find((donor) => donor.id === detailsDonorId) ?? null;

  if (error) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <PageContentWrapper sideMargin={isSmallScreen ? 5 : 25} noSideMargin>
      <MUI.Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3, gap: 2, flexWrap: "wrap" }}
      >
        <MUI.Box>
          <MUI.Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("BloodDonors")}
          </MUI.Typography>
          <MUI.Typography variant="body2" color="text.secondary">
            {t("BloodDonorsSubtitle")}
          </MUI.Typography>
        </MUI.Box>
      </MUI.Stack>

      <MUI.Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, borderRadius: 3, borderColor: "divider" }}
      >
        <MUI.TextField
          value={searchInput}
          onChange={handleSearchInputChange}
          placeholder={t("SearchBloodDonors")}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <MUI.InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </MUI.InputAdornment>
            ),
            endAdornment: searchInput && (
              <MUI.InputAdornment position="end">
                <MUI.IconButton onClick={handleClearSearch} size="small" title={t("Clear")}>
                  <Clear fontSize="small" />
                </MUI.IconButton>
              </MUI.InputAdornment>
            ),
          }}
        />
      </MUI.Paper>

      <MUI.Stack spacing={0}>
        <BloodDonorsTable
          items={visibleDonors}
          isLoading={isLoading}
          emptyMessage={searchParam ? "NoBloodDonorsFoundForGivenQuery" : "NoBloodDonorsFound"}
          onRowClick={(donor) => setDetailsDonorId(donor.id)}
          order={order}
          orderBy={orderBy}
          onSort={handleSort}
        />
        <MUI.TablePagination
          component="div"
          data-testid="clinic.blood-donors.pagination"
          count={sortedDonors.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage={t("RowsPerPage")}
          backIconButtonProps={{
            title: t("PreviousPage"),
            "aria-label": t("PreviousPage"),
          }}
          nextIconButtonProps={{
            title: t("NextPage"),
            "aria-label": t("NextPage"),
          }}
        />
      </MUI.Stack>

      <BloodDonorDetailsDialog
        donor={detailsDonor}
        onClose={() => setDetailsDonorId(null)}
        onViewPet={(donor) => navigate(petViewUrl(donor.id, donor.ownerId))}
      />
    </PageContentWrapper>
  );
}
